import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { documentosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { LowGrandesStatus } from '../../components/admin/LowGrandesStatus';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, CheckCircle, XCircle, 
  HardDrive, RefreshCw, Download,
  Upload, ChevronLeft, ChevronRight,
  AlertTriangle, DollarSign, Calendar, Tag, List,
  BookOpen, Play, Loader2, FileSpreadsheet, X,
  Trash2, Database, StopCircle
} from 'lucide-react';

// Tipos de faltante con sus iconos y colores
const TIPOS_FALTANTE = {
  todos: { label: 'Todos los faltantes', icon: List, color: 'text-gray-600', bg: 'bg-gray-100' },
  pdf: { label: 'Sin PDF', icon: FileText, color: 'text-red-600', bg: 'bg-red-100' },
  modelos: { label: 'Sin modelos', icon: Tag, color: 'text-orange-600', bg: 'bg-orange-100' },
  fecha_revision: { label: 'Sin fecha revisión', icon: Calendar, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  revision: { label: 'Sin nº revisión', icon: AlertTriangle, color: 'text-purple-600', bg: 'bg-purple-100' },
  precio: { label: 'Precio cero', icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-100' }
};

const AdminPDFDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [documentosIncompletos, setDocumentosIncompletos] = useState([]);
  const [conteosPorTipo, setConteosPorTipo] = useState({});
  const [selectedFabricante, setSelectedFabricante] = useState('all');
  const [selectedTipoFaltante, setSelectedTipoFaltante] = useState('todos');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  // Estado para TOC
  const [tocStats, setTocStats] = useState(null);
  const [tocLoading, setTocLoading] = useState(false);
  const [tocProcessing, setTocProcessing] = useState(false);
  const [tocBatchResult, setTocBatchResult] = useState(null);
  
  // Estado para documentos sin índice y carga manual
  const [docsSinIndice, setDocsSinIndice] = useState([]);
  const [docsSinIndiceLoading, setDocsSinIndiceLoading] = useState(false);
  // Paginación de "Sin índice" (Fix 1)
  const [docsSinIndicePage, setDocsSinIndicePage] = useState(1);
  const [docsSinIndicePageSize] = useState(100);
  const [docsSinIndiceTotal, setDocsSinIndiceTotal] = useState(0);
  const [docsSinIndiceTotalPages, setDocsSinIndiceTotalPages] = useState(0);
  const [docsSinIndiceGoToPageInput, setDocsSinIndiceGoToPageInput] = useState('');
  // Estado del diálogo de confirmación para eliminar TOC manual (Fix 2)
  const [docToDeleteToc, setDocToDeleteToc] = useState(null);
  const [deletingToc, setDeletingToc] = useState(false);
  const [selectedDocForToc, setSelectedDocForToc] = useState(null);
  const [uploadingToc, setUploadingToc] = useState(false);
  const fileInputRef = useRef(null);

  // Estado OCR batch
  const [ocrStatus, setOcrStatus] = useState(null);
  const [ocrPolling, setOcrPolling] = useState(false);
  
  // Estado para documentos pendientes grandes
  const [docsPendientes, setDocsPendientes] = useState([]);
  const [docsPendientesLoading, setDocsPendientesLoading] = useState(false);
  const [marcandoSinIndice, setMarcandoSinIndice] = useState(null);

  // Estado para limpieza masiva de cache
  const [bulkCacheStatus, setBulkCacheStatus] = useState(null);
  const [bulkCachePolling, setBulkCachePolling] = useState(false);

  // Estado para pre-cacheo masivo
  const [precacheStatus, setPrecacheStatus] = useState(null);
  const [precachePolling, setPrecachePolling] = useState(false);

  // Estado para pre-cacheo HD
  const [precacheHDStatus, setPrecacheHDStatus] = useState(null);
  const [precacheHDPolling, setPrecacheHDPolling] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadTocStats();
    loadDocsSinIndice();
    loadDocsPendientes();
    checkBulkCacheStatus();
    checkPrecacheStatus();
  }, []);

  // Re-cargar la lista "Sin índice" cuando cambia la página
  useEffect(() => {
    loadDocsSinIndice();
    // eslint-disable-next-line
  }, [docsSinIndicePage]);

  useEffect(() => {
    loadDocumentosIncompletos();
  }, [selectedFabricante, selectedTipoFaltante, pagination.page]);

  // Polling para estado de limpieza masiva
  useEffect(() => {
    if (!bulkCachePolling) return;
    const interval = setInterval(async () => {
      try {
        const res = await documentosAPI.estadoLimpiezaMasiva();
        setBulkCacheStatus(res.data);
        if (!res.data.running) {
          setBulkCachePolling(false);
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [bulkCachePolling]);

  const checkBulkCacheStatus = async () => {
    try {
      const res = await documentosAPI.estadoLimpiezaMasiva();
      setBulkCacheStatus(res.data);
      if (res.data.running) setBulkCachePolling(true);
    } catch { /* ignore */ }
  };

  const handleBulkCacheClear = async () => {
    if (!window.confirm(
      '¿Limpiar cache de páginas de TODOS los documentos?\n\n' +
      'Esto eliminará todas las imágenes renderizadas (disco + R2).\n' +
      'Las páginas se re-renderizarán automáticamente en formato JPEG optimizado cuando los usuarios las abran.\n\n' +
      'Este proceso puede tardar varios minutos.'
    )) return;
    
    try {
      const res = await documentosAPI.limpiarCacheMasivo();
      setBulkCacheStatus({ ...res.data, running: true });
      setBulkCachePolling(true);
      toast.success(`Limpieza iniciada: ${res.data.disk_cleared} archivos de disco eliminados`);
    } catch (e) {
      toast.error('Error al iniciar limpieza masiva');
      console.error(e);
    }
  };

  // Polling para estado de pre-cacheo
  useEffect(() => {
    if (!precachePolling) return;
    const interval = setInterval(async () => {
      try {
        const res = await documentosAPI.estadoPrecacheo();
        setPrecacheStatus(res.data);
        if (!res.data.running) {
          setPrecachePolling(false);
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [precachePolling]);

  const checkPrecacheStatus = async () => {
    try {
      const res = await documentosAPI.estadoPrecacheo();
      setPrecacheStatus(res.data);
      if (res.data.running) setPrecachePolling(true);
    } catch { /* ignore */ }
  };

  const handlePrecacheAll = async () => {
    const savedProgress = precacheStatus?.saved_progress;
    const resumeMsg = savedProgress 
      ? `\n\nSe retomará desde el progreso guardado (${savedProgress.pages_rendered} páginas ya renderizadas).`
      : '';
    if (!window.confirm(
      'Pre-cachear TODAS las páginas de TODOS los documentos en R2.\n\n' +
      'Esto renderizará cada página y la subirá a Cloudflare R2 para que carguen instantáneamente.\n\n' +
      'El proceso puede tardar varias horas.\n' +
      'El progreso se guarda automáticamente y sobrevive reinicios del servidor.\n' +
      'Puede cerrar el navegador, el proceso continuará en el servidor.' +
      resumeMsg
    )) return;
    
    try {
      const res = await documentosAPI.precachearTodo();
      if (res.data.status === 'already_running') {
        toast.info('Ya hay un pre-cacheo en curso');
        setPrecachePolling(true);
        return;
      }
      setPrecacheStatus({ ...res.data, running: true });
      setPrecachePolling(true);
      toast.success(`Pre-cacheo iniciado: ${res.data.total_docs} documentos (~${res.data.total_pages} páginas)`);
    } catch (e) {
      toast.error('Error al iniciar pre-cacheo');
      console.error(e);
    }
  };

  const handleCancelPrecache = async () => {
    if (!window.confirm('¿Cancelar el pre-cacheo en curso?')) return;
    try {
      await documentosAPI.cancelarPrecacheo();
      toast.info('Cancelando pre-cacheo...');
    } catch (e) {
      toast.error('Error al cancelar');
    }
  };

  // === PRE-CACHEO HD ===
  useEffect(() => {
    checkPrecacheHDStatus();
  }, []);

  useEffect(() => {
    if (!precacheHDPolling) return;
    const interval = setInterval(async () => {
      try {
        const res = await documentosAPI.estadoPrecacheoHD();
        setPrecacheHDStatus(res.data);
        if (!res.data.running) setPrecacheHDPolling(false);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [precacheHDPolling]);

  const checkPrecacheHDStatus = async () => {
    try {
      const res = await documentosAPI.estadoPrecacheoHD();
      setPrecacheHDStatus(res.data);
      if (res.data.running) setPrecacheHDPolling(true);
    } catch { /* ignore */ }
  };

  const handlePrecacheHD = async () => {
    const savedProgress = precacheHDStatus?.saved_progress;
    const resumeMsg = savedProgress 
      ? `\n\nSe retomará desde el progreso guardado (${savedProgress.pages_rendered} páginas HD ya renderizadas).`
      : '';
    if (!window.confirm(
      'Pre-cachear versiones HD (alta calidad) de TODOS los documentos.\n\n' +
      'Solo procesa páginas que ya tienen versión LOW en R2.\n' +
      'Las versiones LOW NO se borran — ambas conviven.\n\n' +
      'El proceso es más lento que el pre-cacheo LOW porque las imágenes HD son más pesadas.\n' +
      'El progreso se guarda automáticamente.' +
      resumeMsg
    )) return;
    
    try {
      const res = await documentosAPI.precachearHD();
      if (res.data.status === 'already_running') {
        toast.info(res.data.message);
        setPrecacheHDPolling(true);
        return;
      }
      setPrecacheHDStatus({ ...res.data, running: true });
      setPrecacheHDPolling(true);
      toast.success(`Pre-cacheo HD iniciado: ${res.data.total_docs} documentos`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al iniciar pre-cacheo HD');
    }
  };

  const handleCancelPrecacheHD = async () => {
    if (!window.confirm('¿Cancelar el pre-cacheo HD en curso?')) return;
    try {
      await documentosAPI.cancelarPrecacheoHD();
      toast.info('Cancelando pre-cacheo HD...');
    } catch (e) {
      toast.error('Error al cancelar');
    }
  };

  // === OCR BATCH ===
  useEffect(() => {
    checkOcrStatus();
  }, []);

  useEffect(() => {
    if (!ocrPolling) return;
    const interval = setInterval(async () => {
      try {
        const res = await documentosAPI.estadoOCR();
        setOcrStatus(res.data);
        if (!res.data.running) {
          setOcrPolling(false);
          loadDocsSinIndice();
          loadTocStats();
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [ocrPolling]);

  const checkOcrStatus = async () => {
    try {
      const res = await documentosAPI.estadoOCR();
      setOcrStatus(res.data);
      if (res.data.running) setOcrPolling(true);
    } catch { /* ignore */ }
  };

    const handleStartOCR = async () => {
    if (!window.confirm(
      'Procesar PDFs sin índice.\n\n' +
      'Detecta automáticamente si son escaneados (OCR) o tienen texto extraíble.\n' +
      'El progreso se muestra en tiempo real.\n\n' +
      '¿Iniciar?'
    )) return;

    try {
      const res = await documentosAPI.procesarOCR();
      if (res.data.status === 'already_running') {
        toast.info(res.data.message);
        // Forzar actualización del estado para mostrar progreso
        const statusRes = await documentosAPI.estadoOCR();
        setOcrStatus(statusRes.data);
        setOcrPolling(true);
        return;
      }
      if (res.data.status === 'no_docs') {
        toast.info(res.data.message);
        return;
      }
      setOcrStatus({ ...res.data, running: true });
      setOcrPolling(true);
      toast.success(`Indexación iniciada: ${res.data.total_docs} documentos`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al iniciar');
    }
  };

  const handleCancelOCR = async () => {
    if (!window.confirm('¿Cancelar el proceso OCR?')) return;
    try {
      await documentosAPI.cancelarOCR();
      toast.info('Cancelando OCR...');
    } catch (e) {
      toast.error('Error al cancelar');
    }
  };


  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await documentosAPI.getPdfDashboard();
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadTocStats = async () => {
    try {
      setTocLoading(true);
      const response = await documentosAPI.getEstadisticasToc();
      setTocStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas TOC:', error);
    } finally {
      setTocLoading(false);
    }
  };

  const ejecutarTocBatch = async () => {
    try {
      setTocProcessing(true);
      setTocBatchResult(null);
      
      const response = await documentosAPI.extraerTocBatch(1, true); // Solo cache
      
      setTocBatchResult(response.data);
      await loadTocStats();
      await loadDocsSinIndice();
      
      const conIndice = response.data.con_toc || 0;
      const sinIndice = response.data.sin_toc || 0;
      const omitidos = response.data.omitidos_no_en_cache || 0;
      
      if (conIndice > 0) {
        toast.success(`✅ Índice extraído correctamente`);
      } else if (sinIndice > 0) {
        toast.info(`PDF escaneado (sin índice embebido)`);
      } else if (response.data.procesados === 0 && omitidos > 0) {
        toast.info('No hay PDFs en cache. Se extraerán cuando los usuarios los abran.');
      } else if (response.data.procesados === 0) {
        toast.success('🎉 ¡Todos los documentos procesados!');
      }
      
      if (response.data.pendientes === 0) {
        toast.success('🎉 ¡Completado!');
      }
    } catch (error) {
      console.error('Error ejecutando batch TOC:', error);
      
      const status = error.response?.status;
      if (status === 520 || status === 524) {
        toast.error('Error del servidor. Esperá 1 minuto y recargá la página.');
      } else if (error.code === 'ECONNABORTED') {
        toast.warning('Timeout. El servidor puede estar ocupado.');
      } else {
        toast.error('Error de conexión. Intentá de nuevo.');
      }
      
      // Intentar recargar stats
      try {
        await loadTocStats();
      } catch (e) {
        // Ignorar si falla
      }
    } finally {
      setTocProcessing(false);
    }
  };

  const loadDocsSinIndice = async (pageOverride = null) => {
    try {
      setDocsSinIndiceLoading(true);
      const p = pageOverride ?? docsSinIndicePage;
      const response = await documentosAPI.getDocumentosSinIndice(p, docsSinIndicePageSize);
      setDocsSinIndice(response.data.data || []);
      const pag = response.data.pagination || {};
      setDocsSinIndiceTotal(pag.total || 0);
      setDocsSinIndiceTotalPages(pag.pages || 0);
    } catch (error) {
      console.error('Error cargando docs sin índice:', error);
    } finally {
      setDocsSinIndiceLoading(false);
    }
  };

  const handleEliminarTocManual = async () => {
    if (!docToDeleteToc) return;
    try {
      setDeletingToc(true);
      await documentosAPI.eliminarTocManual(docToDeleteToc.id);
      toast.success(`Índice CSV eliminado de "${docToDeleteToc.numero_referencia}". Podés subirlo de nuevo.`);
      setDocToDeleteToc(null);
      await loadDocsSinIndice();
      await loadTocStats();
    } catch (error) {
      console.error('Error eliminando TOC manual:', error);
      if (error?.response?.status === 409) {
        toast.error('El índice activo ya no es manual; no se eliminó nada.');
        await loadDocsSinIndice();
      } else {
        toast.error('No se pudo eliminar el índice. Revisá la consola.');
      }
    } finally {
      setDeletingToc(false);
    }
  };

  const loadDocsPendientes = async () => {
    try {
      setDocsPendientesLoading(true);
      const response = await documentosAPI.getDocumentosPendientesToc();
      // Ordenar por tamaño descendente (más grandes primero)
      const docs = response.data.documentos || [];
      docs.sort((a, b) => (b.pdf_size_mb || 0) - (a.pdf_size_mb || 0));
      setDocsPendientes(docs);
    } catch (error) {
      console.error('Error cargando docs pendientes:', error);
    } finally {
      setDocsPendientesLoading(false);
    }
  };

  const handleMarcarSinIndice = async (doc) => {
    if (!window.confirm(`¿Marcar "${doc.numero_referencia}" como documento sin índice?\n\nEsto lo agregará a la lista de documentos escaneados para subir CSV manual.`)) {
      return;
    }
    
    try {
      setMarcandoSinIndice(doc.id);
      await documentosAPI.marcarSinIndice(doc.id);
      toast.success(`${doc.numero_referencia} marcado como sin índice`);
      
      // Recargar listas
      await Promise.all([
        loadTocStats(),
        loadDocsSinIndice(),
        loadDocsPendientes()
      ]);
    } catch (error) {
      console.error('Error marcando documento:', error);
      toast.error('Error al marcar el documento');
    } finally {
      setMarcandoSinIndice(null);
    }
  };

  const handleFileSelect = (doc) => {
    setSelectedDocForToc(doc);
    fileInputRef.current?.click();
  };

  /**
   * Parser CSV robusto (RFC 4180) usando PapaParse.
   * Soporta:
   *  - Campos entre comillas dobles con comas internas
   *  - Comillas escapadas dentro de un campo ("")
   *  - UTF-8 con BOM
   *  - Terminaciones de línea Windows (CRLF) y Unix (LF)
   *  - Auto-detección de delimitador (coma, punto y coma o tab)
   *
   * Formato esperado (sin encabezado):
   *   Nivel,Título,Página
   *
   * Retorna: { items: [...], errors: [{row, content, reason}] }
   */
  const parseCSV = (text) => {
    const result = Papa.parse(text, {
      header: false,
      skipEmptyLines: 'greedy',
      dynamicTyping: false,
      // Deja que Papa detecte automáticamente , ; o \t
      delimitersToGuess: [',', ';', '\t'],
    });

    const tocItems = [];
    const errors = [];

    result.data.forEach((row, idx) => {
      const rowNum = idx + 1;

      if (!Array.isArray(row) || row.length === 0) return;

      // Fila con menos de 3 columnas -> inválida
      if (row.length < 3) {
        errors.push({
          row: rowNum,
          content: row.join(' | '),
          reason: `Se esperaban 3 columnas (Nivel, Título, Página) y se encontraron ${row.length}. Revisá las comillas.`,
        });
        return;
      }

      // Fila con más de 3 columnas -> probablemente comillas mal puestas
      if (row.length > 3) {
        errors.push({
          row: rowNum,
          content: row.join(' | '),
          reason: `Se encontraron ${row.length} columnas. Si el título tiene comas, envolvelo entre comillas dobles: "Texto con, comas".`,
        });
        return;
      }

      const rawLevel = String(row[0] ?? '').trim();
      const title = String(row[1] ?? '').trim();
      const rawPage = String(row[2] ?? '').trim();

      const level = parseInt(rawLevel, 10);
      const pageNum = parseInt(rawPage, 10);

      if (!title) {
        errors.push({ row: rowNum, content: row.join(' | '), reason: 'El título está vacío.' });
        return;
      }
      if (isNaN(level) || level < 1) {
        errors.push({ row: rowNum, content: row.join(' | '), reason: `Nivel inválido: "${rawLevel}". Debe ser un entero >= 1.` });
        return;
      }
      if (isNaN(pageNum) || pageNum < 1) {
        errors.push({ row: rowNum, content: row.join(' | '), reason: `Página inválida: "${rawPage}". Debe ser un entero >= 1.` });
        return;
      }

      tocItems.push({
        level: Math.min(Math.max(level, 1), 3),
        title,
        page_num: pageNum,
      });
    });

    return { items: tocItems, errors };
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDocForToc) return;
    
    try {
      setUploadingToc(true);
      
      // Leer el archivo
      const text = await file.text();
      let parseResult = { items: [], errors: [] };
      
      // Detectar formato y parsear
      if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        parseResult = parseCSV(text);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        toast.error('Por ahora solo se aceptan archivos CSV. Exportá tu Excel como CSV.');
        return;
      } else {
        // Intentar parsear como CSV de todas formas
        parseResult = parseCSV(text);
      }
      
      const { items: tocItems, errors: parseErrors } = parseResult;
      
      if (tocItems.length === 0) {
        // Mostrar el primer error concreto si hay alguno
        if (parseErrors.length > 0) {
          const first = parseErrors[0];
          toast.error(
            `No se pudo importar el archivo. Fila ${first.row}: ${first.reason}`,
            { duration: 10000, description: `Contenido: ${first.content.substring(0, 150)}` }
          );
        } else {
          toast.error('No se encontraron entradas válidas en el archivo');
        }
        return;
      }
      
      // Si hubo errores parciales, avisar antes de subir
      if (parseErrors.length > 0) {
        const first = parseErrors[0];
        console.warn('[TOC-CSV] Filas inválidas encontradas:', parseErrors);
        toast.warning(
          `Se importarán ${tocItems.length} entradas. Se saltaron ${parseErrors.length} filas inválidas (ej. fila ${first.row}: ${first.reason})`,
          { duration: 8000 }
        );
      }
      
      // Subir al servidor
      const response = await documentosAPI.subirTocManual(selectedDocForToc.id, tocItems);
      
      if (response.data.success) {
        toast.success(`✅ Índice cargado: ${tocItems.length} entradas`);
        await loadTocStats();
        await loadDocsSinIndice();
      }
    } catch (error) {
      console.error('Error subiendo TOC:', error);
      toast.error('Error al cargar el índice');
    } finally {
      setUploadingToc(false);
      setSelectedDocForToc(null);
      // Limpiar el input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadDocumentosIncompletos = async () => {
    try {
      const fabId = selectedFabricante === 'all' ? null : selectedFabricante;
      const response = await documentosAPI.getDocumentosIncompletos(fabId, selectedTipoFaltante, pagination.page, pagination.limit);
      setDocumentosIncompletos(response.data.data);
      setConteosPorTipo(response.data.conteos_por_tipo || {});
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error cargando documentos incompletos:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
    await loadDocumentosIncompletos();
    await loadTocStats();
    setRefreshing(false);
    toast.success('Dashboard actualizado');
  };

  const exportarLista = () => {
    if (!documentosIncompletos.length) {
      toast.error('No hay documentos para exportar');
      return;
    }

    const csv = [
      ['ID', 'Nombre', 'Referencia', 'Fabricante', 'Faltantes', 'Modelos'].join(','),
      ...documentosIncompletos.map(doc => [
        doc.id,
        `"${doc.nombre?.replace(/"/g, '""') || ''}"`,
        doc.numero_referencia || '',
        `"${doc.fabricante_nombre || ''}"`,
        `"${(doc.faltantes || []).join(', ')}"`,
        `"${(doc.modelos_aplicables || []).join(', ')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `documentos_incompletos_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Lista exportada');
  };

  const irADocumento = (docId) => {
    navigate(`/admin/documentos?doc=${docId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const resumen = dashboardData?.resumen || {};
  const porFabricante = dashboardData?.por_fabricante || [];

  // Calcular totales combinados (R2 + GridFS = Con PDF)
  const totalConPdf = (resumen.con_r2 || 0) + (resumen.con_pdf_gridfs || 0);
  const porcentajeConPdf = resumen.total_documentos > 0 
    ? Math.round((totalConPdf / resumen.total_documentos) * 100) 
    : 0;

  return (
    <div className="space-y-6 p-6" data-testid="pdf-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">📊 Estado de Documentación</h1>
          <p className="text-gray-500">Monitoreo de información incompleta</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Mensaje de estado */}
      <Card className={`border-l-4 ${
        porcentajeConPdf === 100 ? 'border-l-green-500 bg-green-50' :
        porcentajeConPdf >= 50 ? 'border-l-yellow-500 bg-yellow-50' :
        'border-l-blue-500 bg-blue-50'
      }`}>
        <CardContent className="py-4">
          <p className="font-medium text-lg">
            {porcentajeConPdf === 100 
              ? '✅ ¡Todos los documentos tienen PDF!' 
              : `📄 ${resumen.sin_pdf || 0} documentos necesitan PDF`}
          </p>
        </CardContent>
      </Card>

      {/* Tarjetas de conteo por tipo de faltante */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(TIPOS_FALTANTE).filter(([key]) => key !== 'todos').map(([key, config]) => {
          const Icon = config.icon;
          const count = conteosPorTipo[key] || 0;
          const isSelected = selectedTipoFaltante === key;
          
          return (
            <Card 
              key={key}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-teal-500' : ''
              }`}
              onClick={() => {
                setSelectedTipoFaltante(key);
                setPagination(p => ({...p, page: 1}));
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`p-1.5 rounded ${config.bg}`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{config.label}</span>
                </div>
                <p className={`text-2xl font-bold ${count > 0 ? config.color : 'text-green-600'}`}>
                  {count}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Barra de Progreso de PDFs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <HardDrive className="h-5 w-5" />
            Progreso de Carga de PDFs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{totalConPdf} de {resumen.total_documentos || 0} documentos con PDF</span>
              <span className="font-bold">{porcentajeConPdf}%</span>
            </div>
            <Progress value={porcentajeConPdf} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* === PRE-CACHEO MASIVO EN R2 === */}
      <Card className="border-2 border-teal-200" data-testid="precache-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-teal-600" />
            Pre-cachear Documentos en R2
          </CardTitle>
          <CardDescription>
            Renderiza todas las páginas de todos los documentos y las sube a R2 para carga instantánea
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progreso del pre-cacheo */}
          {precacheStatus?.running && precacheStatus?.modalidad !== 'low_grandes' && (
            <div className="space-y-3" data-testid="precache-progress">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-teal-700">
                  Doc {precacheStatus.docs_processed + 1}/{precacheStatus.total_docs}
                </span>
                <span className="text-sm font-bold text-teal-800">{precacheStatus.porcentaje || 0}%</span>
              </div>
              <Progress value={precacheStatus.porcentaje || 0} className="h-3" />
              <div className="text-xs text-teal-600 space-y-1">
                <p className="font-medium truncate">{precacheStatus.current_doc}</p>
                {precacheStatus.current_page > 0 && (
                  <p>Página {precacheStatus.current_page}/{precacheStatus.current_doc_pages}</p>
                )}
                <div className="flex gap-4 text-xs">
                  <span>Renderizadas: {precacheStatus.pages_rendered}</span>
                  <span>Ya existían: {precacheStatus.pages_skipped}</span>
                  {precacheStatus.pages_failed > 0 && (
                    <span className="text-red-600">Fallaron: {precacheStatus.pages_failed}</span>
                  )}
                </div>
                {precacheStatus.memory_available_mb && (
                  <p className={`font-medium ${precacheStatus.memory_available_mb < 400 ? 'text-red-600' : 'text-teal-600'}`}>
                    RAM contenedor: {precacheStatus.memory_available_mb} MB disponible / {precacheStatus.memory_limit_mb || '?'} MB límite
                    {precacheStatus.memory_source === 'cgroup' && <span className="text-xs ml-1">(cgroup)</span>}
                    {precacheStatus.memory_source === 'psutil_fallback' && <span className="text-xs ml-1 text-amber-600">(psutil - puede ser inexacto)</span>}
                    {precacheStatus.memory_inactive_file_mb > 0 && (
                      <span className="text-xs ml-1 text-gray-500">
                        (cache reclamable: {precacheStatus.memory_inactive_file_mb} MB)
                      </span>
                    )}
                  </p>
                )}
                {precacheStatus.docs_skipped_large > 0 && (
                  <p className="text-amber-600">
                    PDFs grandes saltados: {precacheStatus.docs_skipped_large} (se cachearán manualmente)
                  </p>
                )}
                {precacheStatus.elapsed_seconds > 0 && (
                  <p>Tiempo: {Math.floor(precacheStatus.elapsed_seconds / 60)}m {precacheStatus.elapsed_seconds % 60}s</p>
                )}
              </div>
            </div>
          )}

          {/* Listas de documentos con problemas (para saber cuáles revisar) */}
          {precacheStatus?.skipped_large_docs?.length > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="precache-skipped-large-list">
              <p className="text-sm font-semibold text-amber-800">
                PDFs grandes saltados ({precacheStatus.skipped_large_docs.length}) — renderizar con el botón individual
              </p>
              <ul className="mt-1 max-h-40 overflow-auto text-xs text-amber-700 space-y-0.5">
                {precacheStatus.skipped_large_docs.map((d) => (
                  <li key={d.id}>• {d.nombre} <span className="text-amber-500">({d.motivo})</span></li>
                ))}
              </ul>
            </div>
          )}
          {precacheStatus?.failed_docs?.length > 0 && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3" data-testid="precache-failed-list">
              <p className="text-sm font-semibold text-red-800">
                Documentos que fallaron ({precacheStatus.failed_docs.length}) — se reintentan solos al terminar; si siguen acá, revisalos
              </p>
              <ul className="mt-1 max-h-40 overflow-auto text-xs text-red-700 space-y-0.5">
                {precacheStatus.failed_docs.map((d) => (
                  <li key={d.id}>• {d.nombre} <span className="text-red-500">({d.motivo})</span></li>
                ))}
              </ul>
            </div>
          )}


          {/* Resultado final */}
          {precacheStatus && !precacheStatus.running && precacheStatus.finished_at && precacheStatus.modalidad !== 'low_grandes' && (
            <div className={`p-3 rounded-lg text-sm ${precacheStatus.error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
              {precacheStatus.error ? (
                <p>{precacheStatus.error}</p>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium">Pre-cacheo completado</p>
                  <p>{precacheStatus.pages_rendered} páginas renderizadas, {precacheStatus.pages_skipped} ya existían{precacheStatus.pages_failed > 0 ? `, ${precacheStatus.pages_failed} fallaron` : ''}</p>
                  {precacheStatus.elapsed_seconds > 0 && (
                    <p>Duración: {Math.floor(precacheStatus.elapsed_seconds / 3600)}h {Math.floor((precacheStatus.elapsed_seconds % 3600) / 60)}m</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Progreso guardado (si hay progreso previo y no está corriendo) */}
          {precacheStatus?.saved_progress && !precacheStatus?.running && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm space-y-2" data-testid="saved-progress-info">
              <p className="font-medium text-blue-800">Progreso guardado de ejecución anterior:</p>
              <div className="flex gap-4 text-xs text-blue-700">
                <span>Renderizadas: {precacheStatus.saved_progress.pages_rendered}</span>
                <span>Saltadas: {precacheStatus.saved_progress.pages_skipped}</span>
                {precacheStatus.saved_progress.pages_failed > 0 && (
                  <span className="text-red-600">Fallidas: {precacheStatus.saved_progress.pages_failed}</span>
                )}
                <span>Docs: {precacheStatus.saved_progress.docs_processed}</span>
              </div>
              <p className="text-xs text-blue-600">Al iniciar, el proceso retomará desde donde se quedó.</p>
            </div>
          )}

          {/* Info */}
          <div className="text-sm bg-teal-50 p-3 rounded-lg space-y-1">
            <p className="font-medium text-teal-800">Qué hace este botón:</p>
            <ul className="text-teal-700 text-xs space-y-1 list-disc list-inside">
              <li>Renderiza cada página de cada documento y la sube a Cloudflare R2</li>
              <li>Las páginas que ya están en R2 se omiten automáticamente</li>
              <li>Puede cerrar el navegador, el proceso sigue en el servidor</li>
              <li>El progreso se guarda automáticamente — sobrevive reinicios del servidor</li>
              <li>Usa lectura cgroup para monitorear la RAM REAL del contenedor</li>
            </ul>
          </div>

          {/* Botones */}
          <div className="flex gap-2">
            <Button
              onClick={handlePrecacheAll}
              disabled={precacheStatus?.running}
              className="flex-1 bg-teal-600 hover:bg-teal-700 h-12 text-base"
              data-testid="precache-all-btn"
            >
              {precacheStatus?.running ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Pre-cacheando... {precacheStatus.porcentaje || 0}%
                </>
              ) : (
                <>
                  <Database className="h-5 w-5 mr-2" />
                  Pre-cachear Todos los Documentos
                </>
              )}
            </Button>
            {precacheStatus?.running && (
              <Button
                onClick={handleCancelPrecache}
                variant="outline"
                className="h-12 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="cancel-precache-btn"
              >
                <StopCircle className="h-5 w-5" />
              </Button>
            )}
            {precacheStatus?.saved_progress && !precacheStatus?.running && (
              <Button
                onClick={async () => {
                  if (!window.confirm('¿Limpiar progreso guardado? El próximo pre-cacheo empezará desde el primer documento.')) return;
                  try {
                    await documentosAPI.limpiarProgresoPrecacheo();
                    toast.success('Progreso limpiado');
                    checkPrecacheStatus();
                  } catch (e) {
                    toast.error('Error al limpiar progreso');
                  }
                }}
                variant="outline"
                className="h-12 border-amber-300 text-amber-600 hover:bg-amber-50 text-xs"
                data-testid="clear-progress-btn"
              >
                Empezar desde cero
              </Button>
            )}
          </div>

          {/* Estado persistente LOW PDFs grandes */}
          <LowGrandesStatus grandes={precacheStatus?.grandes} />

          {/* Botón PDFs grandes */}
          {!precacheStatus?.running && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
              <p className="text-sm font-medium text-amber-800">
                Pre-cacheo de PDFs Grandes (&gt;150MB)
              </p>
              <p className="text-xs text-amber-600">
                Modo conservador: zoom reducido, pausas largas entre páginas, más control de memoria.
                Este proceso es más lento pero seguro para PDFs pesados que el script normal salta.
              </p>
              <Button
                onClick={async () => {
                  if (!window.confirm(
                    'Pre-cachear SOLO los PDFs grandes (>150MB).\n\n' +
                    'Modo conservador: zoom reducido, 5 seg entre páginas, 15 seg entre documentos.\n' +
                    'Este proceso es más lento pero evita problemas de memoria.\n\n' +
                    '¿Iniciar?'
                  )) return;
                  try {
                    const res = await documentosAPI.precachearGrandes();
                    if (res.data?.status === 'started') {
                      toast.success(res.data.message || 'Pre-cacheo de PDFs grandes iniciado');
                    } else {
                      toast.warning(res.data?.message || 'No se inició el pre-cacheo de PDFs grandes');
                    }
                    checkPrecacheStatus();
                  } catch (e) {
                    toast.error(e.response?.data?.message || 'Error al iniciar');
                  }
                }}
                className="w-full bg-amber-600 hover:bg-amber-700 h-10"
                data-testid="precache-large-btn"
              >
                <Database className="h-4 w-4 mr-2" />
                Pre-cachear PDFs Grandes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === PRE-CACHEO HD (ALTA CALIDAD) === */}
      <Card className="border-2 border-indigo-200" data-testid="precache-hd-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-indigo-600" />
            Pre-cachear HD (Alta Calidad)
          </CardTitle>
          <CardDescription>
            Genera versiones HD de todas las páginas que ya tienen versión LOW en R2. Ambas versiones conviven.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progreso HD en curso */}
          {precacheHDStatus?.running && (
            <div className="space-y-3" data-testid="precache-hd-progress">
              <div className="flex justify-between items-center">
                <span className="text-sm text-indigo-600">
                  Doc {(precacheHDStatus.docs_processed || 0) + 1}/{precacheHDStatus.total_docs}
                </span>
                <span className="text-sm font-bold text-indigo-800">{precacheHDStatus.porcentaje || 0}%</span>
              </div>
              <Progress value={precacheHDStatus.porcentaje || 0} className="h-3" />
              <div className="text-xs text-gray-600 space-y-1">
                <p className="font-medium truncate">{precacheHDStatus.current_doc}</p>
                {precacheHDStatus.current_page > 0 && (
                  <p>Página {precacheHDStatus.current_page}/{precacheHDStatus.current_doc_pages}</p>
                )}
                <div className="flex gap-4 flex-wrap">
                  <span>HD Renderizadas: {precacheHDStatus.pages_rendered}</span>
                  <span>Ya existían: {precacheHDStatus.pages_skipped}</span>
                  {precacheHDStatus.pages_no_low > 0 && (
                    <span className="text-amber-600">Sin LOW: {precacheHDStatus.pages_no_low}</span>
                  )}
                  {precacheHDStatus.pages_failed > 0 && (
                    <span className="text-red-600">Fallaron: {precacheHDStatus.pages_failed}</span>
                  )}
                </div>
                {precacheHDStatus.memory_available_mb && (
                  <p className={`font-medium ${precacheHDStatus.memory_available_mb < 500 ? 'text-red-600' : 'text-indigo-600'}`}>
                    RAM: {precacheHDStatus.memory_available_mb} MB libre / {precacheHDStatus.memory_limit_mb || '?'} MB
                  </p>
                )}
                {precacheHDStatus.elapsed_seconds > 0 && (
                  <p>Tiempo: {Math.floor(precacheHDStatus.elapsed_seconds / 60)}m {precacheHDStatus.elapsed_seconds % 60}s</p>
                )}
              </div>
            </div>
          )}

          {/* Resultado final HD */}
          {precacheHDStatus && !precacheHDStatus.running && precacheHDStatus.finished_at && (
            <div className={`p-3 rounded-lg text-sm ${precacheHDStatus.error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
              {precacheHDStatus.error ? (
                <p>{precacheHDStatus.error}</p>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium">Pre-cacheo HD completado</p>
                  <p>{precacheHDStatus.pages_rendered} páginas HD renderizadas, {precacheHDStatus.pages_skipped} ya existían{precacheHDStatus.pages_failed > 0 ? `, ${precacheHDStatus.pages_failed} fallaron` : ''}</p>
                  {precacheHDStatus.elapsed_seconds > 0 && (
                    <p>Duración: {Math.floor(precacheHDStatus.elapsed_seconds / 3600)}h {Math.floor((precacheHDStatus.elapsed_seconds % 3600) / 60)}m</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Progreso guardado HD */}
          {precacheHDStatus?.saved_progress && !precacheHDStatus?.running && (
            <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm space-y-2">
              <p className="font-medium text-indigo-800">Progreso HD guardado:</p>
              <div className="flex gap-4 text-xs text-indigo-700">
                <span>HD Renderizadas: {precacheHDStatus.saved_progress.pages_rendered}</span>
                <span>Saltadas: {precacheHDStatus.saved_progress.pages_skipped}</span>
                <span>Docs: {precacheHDStatus.saved_progress.docs_processed}</span>
              </div>
              <p className="text-xs text-indigo-600">Al iniciar, retomará desde donde se quedó.</p>
            </div>
          )}

          {/* Info */}
          <div className="text-sm bg-indigo-50 p-3 rounded-lg space-y-1">
            <p className="font-medium text-indigo-800">Qué hace este botón:</p>
            <ul className="text-indigo-700 text-xs space-y-1 list-disc list-inside">
              <li>Genera versiones HD (zoom 1.5x) de cada página y las sube a R2</li>
              <li>Solo procesa páginas que YA tienen versión LOW — no borra las LOW</li>
              <li>PDFs grandes (&gt;100MB): pausas más largas entre páginas para evitar OOM</li>
              <li>El progreso se guarda automáticamente — sobrevive reinicios</li>
              <li>Los usuarios verán carga rápida (LOW) + mejora automática a HD</li>
            </ul>
          </div>

          {/* Botones HD */}
          <div className="flex gap-2">
            <Button
              onClick={handlePrecacheHD}
              disabled={precacheHDStatus?.running || precacheStatus?.running}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
              data-testid="precache-hd-btn"
            >
              {precacheHDStatus?.running ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Pre-cacheando HD... {precacheHDStatus.porcentaje || 0}%
                </>
              ) : (
                <>
                  <Database className="h-5 w-5 mr-2" />
                  Pre-cachear HD
                </>
              )}
            </Button>
            {precacheHDStatus?.running && (
              <Button
                onClick={handleCancelPrecacheHD}
                variant="outline"
                className="h-12 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="cancel-precache-hd-btn"
              >
                <StopCircle className="h-5 w-5" />
              </Button>
            )}
            {precacheHDStatus?.saved_progress && !precacheHDStatus?.running && (
              <Button
                onClick={async () => {
                  if (!window.confirm('¿Limpiar progreso HD guardado?')) return;
                  try {
                    await documentosAPI.limpiarProgresoPrecacheoHD();
                    toast.success('Progreso HD limpiado');
                    checkPrecacheHDStatus();
                  } catch (e) {
                    toast.error('Error al limpiar progreso HD');
                  }
                }}
                variant="outline"
                className="h-12 border-amber-300 text-amber-600 hover:bg-amber-50 text-xs"
                data-testid="clear-hd-progress-btn"
              >
                Desde cero
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* === LIMPIEZA MASIVA DE CACHE === */}
      <Card className="border-2 border-orange-200" data-testid="bulk-cache-clear-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-orange-600" />
            Limpieza Masiva de Cache de Páginas
          </CardTitle>
          <CardDescription>
            Elimina todas las imágenes renderizadas (disco + R2) para forzar el re-renderizado en formato JPEG optimizado.
            Útil para corregir páginas cortadas o actualizar el formato de renderizado.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Estado de limpieza en curso */}
          {bulkCacheStatus?.running && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-orange-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Limpiando R2: {bulkCacheStatus.current_doc}</span>
              </div>
              <Progress value={bulkCacheStatus.porcentaje || 0} className="h-2" />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{bulkCacheStatus.processed} / {bulkCacheStatus.total_docs} documentos</span>
                <span>{bulkCacheStatus.r2_cleared} archivos R2 eliminados</span>
              </div>
            </div>
          )}

          {/* Resultado de última limpieza */}
          {bulkCacheStatus && !bulkCacheStatus.running && bulkCacheStatus.finished_at && (
            <div className="bg-green-50 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-800">Última limpieza completada</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="font-bold text-gray-800">{bulkCacheStatus.total_docs}</p>
                  <p className="text-xs text-gray-500">Documentos</p>
                </div>
                <div>
                  <p className="font-bold text-gray-800">{bulkCacheStatus.disk_cleared}</p>
                  <p className="text-xs text-gray-500">Disco eliminados</p>
                </div>
                <div>
                  <p className="font-bold text-gray-800">{bulkCacheStatus.r2_cleared}</p>
                  <p className="text-xs text-gray-500">R2 eliminados</p>
                </div>
              </div>
              {bulkCacheStatus.elapsed_seconds > 0 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Duración: {Math.floor(bulkCacheStatus.elapsed_seconds / 60)}m {bulkCacheStatus.elapsed_seconds % 60}s
                </p>
              )}
            </div>
          )}

          {/* Info */}
          <div className="text-sm bg-orange-50 p-3 rounded-lg space-y-1">
            <p className="font-medium text-orange-800">Qué hace este botón:</p>
            <ul className="text-orange-700 text-xs space-y-1 list-disc list-inside">
              <li>Elimina TODAS las imágenes de páginas del disco local (instantáneo)</li>
              <li>Elimina TODAS las imágenes cacheadas en Cloudflare R2 (puede tardar minutos)</li>
              <li>Al abrir un documento, las páginas se renderizarán en JPEG optimizado</li>
              <li>Resuelve problemas de páginas cortadas causados por cache antiguo en formato PNG</li>
            </ul>
          </div>

          {/* Botón */}
          <Button
            onClick={handleBulkCacheClear}
            disabled={bulkCacheStatus?.running}
            className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-base"
            data-testid="bulk-cache-clear-btn"
          >
            {bulkCacheStatus?.running ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Limpiando... {bulkCacheStatus.porcentaje || 0}%
              </>
            ) : (
              <>
                <Trash2 className="h-5 w-5 mr-2" />
                Limpiar Cache de Todos los Documentos
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* === GESTIÓN DE ÍNDICES (TOC) === */}
      <Card className="border-2 border-teal-200" data-testid="toc-management-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-teal-600" />
            Gestión de Índices (TOC)
          </CardTitle>
          <CardDescription>
            Extrae automáticamente la tabla de contenidos de los PDFs para mejorar la navegación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Estadísticas del TOC */}
          {tocLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
            </div>
          ) : tocStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-800">{tocStats.total_documentos_con_pdf}</p>
                <p className="text-xs text-gray-500">Total con PDF</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{tocStats.con_toc_extraido}</p>
                <p className="text-xs text-gray-500">Con índice</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-yellow-600">{tocStats.sin_indice_embebido}</p>
                <p className="text-xs text-gray-500">Sin índice (escaneados)</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{tocStats.pendientes_de_procesar}</p>
                <p className="text-xs text-gray-500">Pendientes</p>
              </div>
            </div>
          ) : null}

          {/* Barra de progreso del TOC */}
          {tocStats && tocStats.total_documentos_con_pdf > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progreso de extracción</span>
                <span className="font-bold">{tocStats.porcentaje_procesado}%</span>
              </div>
              <Progress value={tocStats.porcentaje_procesado} className="h-2" />
              <p className="text-xs text-gray-500">
                {tocStats.porcentaje_con_indice}% de los documentos tienen índice navegable
              </p>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={() => ejecutarTocBatch()}
              disabled={tocProcessing || (tocStats?.pendientes_de_procesar === 0)}
              className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-base"
              data-testid="toc-batch-btn"
            >
              {tocProcessing ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : tocStats?.pendientes_de_procesar === 0 ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  ¡Todos los documentos procesados!
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Extraer Índice (1 documento)
                </>
              )}
            </Button>
            
            {tocStats?.pendientes_de_procesar > 0 && (
              <p className="text-sm text-center text-gray-600">
                Quedan <strong>{tocStats.pendientes_de_procesar}</strong> documentos.
                <br />
                <span className="text-xs text-gray-500">Hacé click en el botón por cada documento.</span>
              </p>
            )}
          </div>

          {/* Resultado del último batch */}
          {tocBatchResult && (
            <div className="bg-gray-50 rounded-lg p-3 mt-3">
              <p className="font-medium text-sm mb-2">Último resultado:</p>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <p className="font-bold text-green-600">{tocBatchResult.con_toc}</p>
                  <p className="text-xs text-gray-500">Con índice</p>
                </div>
                <div>
                  <p className="font-bold text-yellow-600">{tocBatchResult.sin_toc}</p>
                  <p className="text-xs text-gray-500">Sin índice</p>
                </div>
                <div>
                  <p className="font-bold text-blue-600">{tocBatchResult.pendientes}</p>
                  <p className="text-xs text-gray-500">Pendientes</p>
                </div>
              </div>
              {tocBatchResult.nota && (
                <p className="text-xs text-gray-500 mt-2 italic">{tocBatchResult.nota}</p>
              )}
            </div>
          )}

          {/* Mensaje informativo */}
          <div className="text-sm bg-blue-50 p-3 rounded-lg space-y-2">
            <p className="font-medium text-blue-800">💡 ¿Cómo funciona?</p>
            <ul className="text-blue-700 text-xs space-y-1 list-disc list-inside">
              <li><strong>Los índices se guardan en la base de datos</strong> - Una vez extraídos, estarán disponibles para TODOS los usuarios.</li>
              <li><strong>Extracción automática</strong> - Los documentos que no proceses aquí se extraerán automáticamente cuando un usuario los abra.</li>
              <li><strong>PDFs escaneados</strong> - Algunos PDFs no tienen índice porque son imágenes escaneadas. Para estos se muestra "Navegación Rápida".</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* === DOCUMENTOS PENDIENTES GRANDES (>80MB) === */}
      {docsPendientes.filter(d => d.pdf_size_mb >= 80).length > 0 && (
        <Card className="border-2 border-red-200" data-testid="docs-pendientes-grandes-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              PDFs Muy Grandes - Acción Manual Requerida ({docsPendientes.filter(d => d.pdf_size_mb >= 80).length})
            </CardTitle>
            <CardDescription>
              Estos PDFs son demasiado grandes para procesar automáticamente (causan timeout). 
              <strong className="text-red-600"> Marcalos como "sin índice"</strong> para poder subir un CSV manual después.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Advertencia */}
            <div className="bg-red-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-red-800 mb-1">⚠️ PDFs mayores a 80MB</p>
              <p className="text-red-700 text-xs">
                Cloudflare corta las conexiones antes de que estos archivos terminen de descargarse.
                Al marcarlos como "sin índice", aparecerán en la lista de abajo para subir CSV.
              </p>
            </div>
            
            {/* Lista de documentos grandes */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {docsPendientesLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-red-600" />
                </div>
              ) : (
                docsPendientes
                  .filter(d => d.pdf_size_mb >= 80)
                  .map((doc) => (
                    <div 
                      key={doc.id} 
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.numero_referencia}</p>
                        <p className="text-xs text-gray-500 truncate">{doc.nombre}</p>
                        <p className="text-xs font-bold text-red-600">{doc.pdf_size_mb} MB</p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleMarcarSinIndice(doc)}
                        disabled={marcandoSinIndice === doc.id}
                        className="text-xs ml-2"
                      >
                        {marcandoSinIndice === doc.id ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1" />
                        )}
                        Marcar sin índice
                      </Button>
                    </div>
                  ))
              )}
            </div>
            
            {/* Botón para marcar todos */}
            {docsPendientes.filter(d => d.pdf_size_mb >= 80).length > 1 && (
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-50"
                onClick={async () => {
                  if (!window.confirm(`¿Marcar TODOS los ${docsPendientes.filter(d => d.pdf_size_mb >= 80).length} documentos grandes como "sin índice"?`)) return;
                  
                  const grandes = docsPendientes.filter(d => d.pdf_size_mb >= 80);
                  for (const doc of grandes) {
                    try {
                      setMarcandoSinIndice(doc.id);
                      await documentosAPI.marcarSinIndice(doc.id);
                    } catch (e) {
                      console.error('Error:', e);
                    }
                  }
                  setMarcandoSinIndice(null);
                  toast.success(`${grandes.length} documentos marcados como sin índice`);
                  await Promise.all([loadTocStats(), loadDocsSinIndice(), loadDocsPendientes()]);
                }}
                disabled={marcandoSinIndice !== null}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Marcar todos como sin índice ({docsPendientes.filter(d => d.pdf_size_mb >= 80).length})
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* === OCR PARA PDFs ESCANEADOS === */}
      <Card className="border-2 border-purple-200" data-testid="ocr-batch-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            OCR - Reconocimiento de Texto
          </CardTitle>
          <CardDescription>
            Procesa PDFs sin índice: detecta automáticamente si son escaneados (OCR) o tienen texto extraíble (indexación directa).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progreso OCR */}
          {ocrStatus?.running && (
            <div className="space-y-3" data-testid="ocr-progress">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">
                  Doc {(ocrStatus.docs_processed || 0) + 1}/{ocrStatus.total_docs}
                </span>
                <span className="text-sm font-bold text-purple-800">{ocrStatus.porcentaje || 0}%</span>
              </div>
              <Progress value={ocrStatus.porcentaje || 0} className="h-3" />
              <div className="text-xs text-gray-600 space-y-1">
                <p className="font-medium truncate">{ocrStatus.current_doc}</p>
                <div className="flex gap-4 flex-wrap">
                  <span className="text-green-600">Exitosos: {ocrStatus.docs_success}</span>
                  {ocrStatus.docs_texto_directo > 0 && (
                    <span className="text-blue-600">Texto directo: {ocrStatus.docs_texto_directo}</span>
                  )}
                  {ocrStatus.docs_ocr > 0 && (
                    <span className="text-purple-600">OCR: {ocrStatus.docs_ocr}</span>
                  )}
                  {ocrStatus.docs_failed > 0 && (
                    <span className="text-red-600">Fallaron: {ocrStatus.docs_failed}</span>
                  )}
                  <span>Páginas indexadas: {ocrStatus.total_pages_ocr}</span>
                </div>
                {ocrStatus.memory_available_mb && (
                  <p className={ocrStatus.memory_available_mb < 400 ? 'text-red-600 font-medium' : 'text-purple-600'}>
                    RAM: {ocrStatus.memory_available_mb} MB libre
                  </p>
                )}
                {ocrStatus.elapsed_seconds > 0 && (
                  <p>Tiempo: {Math.floor(ocrStatus.elapsed_seconds / 60)}m {ocrStatus.elapsed_seconds % 60}s</p>
                )}
              </div>
            </div>
          )}

          {/* Resultado final */}
          {ocrStatus && !ocrStatus.running && ocrStatus.finished_at && (
            <div className={`p-3 rounded-lg text-sm ${ocrStatus.error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
              {ocrStatus.error ? (
                <p>{ocrStatus.error}</p>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium">OCR completado</p>
                  <p>{ocrStatus.docs_success} documentos procesados ({ocrStatus.detalle_metodos || `${ocrStatus.docs_success} total`}), {ocrStatus.total_pages_ocr} páginas indexadas</p>
                  {ocrStatus.docs_failed > 0 && <p className="text-amber-700">{ocrStatus.docs_failed} documentos fallaron</p>}
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="text-sm bg-purple-50 p-3 rounded-lg space-y-1">
            <p className="font-medium text-purple-800">Qué hace este botón:</p>
            <ul className="text-purple-700 text-xs space-y-1 list-disc list-inside">
              <li>Detecta automáticamente si cada PDF es escaneado o tiene texto</li>
              <li>PDFs con texto: indexa directamente (rápido) + extrae TOC si existe</li>
              <li>PDFs escaneados: aplica OCR (Tesseract, inglés) página por página</li>
              <li>El buscador del visor encontrará texto en todos los documentos procesados</li>
              <li>Control de memoria automático — cancelación responsiva</li>
            </ul>
          </div>

          {/* Botones */}
          <div className="flex gap-2">
            <Button
              onClick={handleStartOCR}
              disabled={ocrStatus?.running}
              className="flex-1 bg-purple-600 hover:bg-purple-700 h-12 text-base"
              data-testid="start-ocr-btn"
            >
              {ocrStatus?.running ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Procesando... {ocrStatus.porcentaje || 0}%
                </>
              ) : (
                <>
                  <FileText className="h-5 w-5 mr-2" />
                  Procesar e indexar ({docsSinIndice.length} PDFs)
                </>
              )}
            </Button>
            {ocrStatus?.running && (
              <Button
                onClick={handleCancelOCR}
                variant="outline"
                className="h-12 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="cancel-ocr-btn"
              >
                <StopCircle className="h-5 w-5" />
              </Button>
            )}
            {ocrStatus?.running && (
              <Button
                onClick={async () => {
                  if (!window.confirm('¿Forzar reseteo del estado? Use esto solo si el proceso quedó colgado después de un despliegue.')) return;
                  try {
                    await documentosAPI.resetearOCR();
                    toast.success('Estado reseteado');
                    setOcrPolling(false);
                    checkOcrStatus();
                  } catch (e) {
                    toast.error('Error al resetear');
                  }
                }}
                variant="outline"
                className="h-12 border-amber-300 text-amber-600 hover:bg-amber-50 text-xs"
                data-testid="reset-ocr-btn"
                title="Usar si el proceso quedó colgado"
              >
                Resetear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* === DOCUMENTOS SIN ÍNDICE (ESCANEADOS) === */}
      {(docsSinIndice.length > 0 || docsSinIndiceTotal > 0) && (
        <Card className="border-2 border-amber-200" data-testid="docs-sin-indice-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-amber-600" />
              PDFs sin Índice ({docsSinIndiceTotal})
            </CardTitle>
            <CardDescription>
              Estos documentos son PDFs escaneados o no tienen índice embebido. 
              Podés subir un índice manual desde un archivo CSV.
              {docsSinIndiceTotalPages > 1 && (
                <span className="block mt-1 text-xs text-amber-700">
                  Mostrando {docsSinIndice.length} de {docsSinIndiceTotal} — Página {docsSinIndicePage} de {docsSinIndiceTotalPages}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Input oculto para subir archivo */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            
            {/* Instrucciones */}
            <div className="bg-amber-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-amber-800 mb-2">📋 Formato del archivo CSV:</p>
              <div className="bg-white p-2 rounded font-mono text-xs text-gray-700">
                <p>Nivel, Título, Página</p>
                <p>1, Capítulo 1 - Introducción, 1</p>
                <p>2, 1.1 Descripción General, 2</p>
                <p>2, 1.2 Especificaciones, 5</p>
                <p>1, Capítulo 2 - Mantenimiento, 10</p>
              </div>
              <p className="text-amber-700 mt-2 text-xs">
                <strong>Nivel:</strong> 1 = Título principal, 2 = Subtítulo, 3 = Sub-subtítulo
              </p>
            </div>
            
            {/* Lista de documentos */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {docsSinIndiceLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                </div>
              ) : (
                docsSinIndice.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{doc.numero_referencia}</p>
                      <p className="text-xs text-gray-500 truncate">{doc.nombre}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {doc.tiene_toc_manual ? (
                        <>
                          {doc.toc_tipo === 'array' && doc.toc_entradas > 0 ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 text-xs" title="Importación pendiente de validación" data-testid={`badge-manual-${doc.id}`}>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              CSV manual: {doc.toc_entradas} entradas
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-xs" title="El visor usa navegación por páginas" data-testid={`badge-manual-vacio-${doc.id}`}>
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Marca manual sin entradas
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                            onClick={() => setDocToDeleteToc(doc)}
                            disabled={deletingToc}
                            title="Eliminar índice CSV"
                            data-testid={`eliminar-toc-btn-${doc.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleFileSelect(doc)}
                          disabled={uploadingToc}
                          className="text-xs"
                          data-testid={`subir-csv-btn-${doc.id}`}
                        >
                          {uploadingToc && selectedDocForToc?.id === doc.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Upload className="h-3 w-3 mr-1" />
                          )}
                          Subir CSV
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Controles de paginación */}
            {docsSinIndiceTotalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-amber-100">
                <div className="text-xs text-gray-600">
                  Página <strong>{docsSinIndicePage}</strong> de <strong>{docsSinIndiceTotalPages}</strong> — Total: {docsSinIndiceTotal} PDFs
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDocsSinIndicePage(p => Math.max(1, p - 1))}
                    disabled={docsSinIndicePage === 1 || docsSinIndiceLoading}
                    data-testid="sin-indice-page-prev"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                  </Button>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Ir a:</span>
                    <input
                      type="number"
                      min="1"
                      max={docsSinIndiceTotalPages}
                      value={docsSinIndiceGoToPageInput}
                      onChange={(e) => setDocsSinIndiceGoToPageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const n = parseInt(docsSinIndiceGoToPageInput);
                          if (n >= 1 && n <= docsSinIndiceTotalPages) {
                            setDocsSinIndicePage(n);
                            setDocsSinIndiceGoToPageInput('');
                          }
                        }
                      }}
                      className="w-16 px-2 py-1 border rounded text-sm"
                      placeholder={docsSinIndicePage.toString()}
                      data-testid="sin-indice-goto-input"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDocsSinIndicePage(p => Math.min(docsSinIndiceTotalPages, p + 1))}
                    disabled={docsSinIndicePage === docsSinIndiceTotalPages || docsSinIndiceLoading}
                    data-testid="sin-indice-page-next"
                  >
                    Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diálogo de confirmación de eliminación de TOC manual */}
      <AlertDialog open={!!docToDeleteToc} onOpenChange={(open) => !open && setDocToDeleteToc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar índice CSV?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Vas a eliminar el índice CSV subido para <strong>{docToDeleteToc?.numero_referencia}</strong>.
                </p>
                <p>
                  El documento va a reaparecer en la lista <strong>"Sin Índice"</strong> con el botón "Subir CSV" para que puedas cargar el archivo corregido.
                </p>
                <p className="text-gray-600 text-xs">
                  Esta acción no afecta al PDF ni al resto del documento — solo elimina el índice manual.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="eliminar-toc-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEliminarTocManual}
              disabled={deletingToc}
              className="bg-red-600 hover:bg-red-700"
              data-testid="eliminar-toc-confirm"
            >
              {deletingToc ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Eliminando…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-1" /> Sí, eliminar índice</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Estadísticas por Fabricante */}
      <Card>
        <CardHeader>
          <CardTitle>📦 Estado por Fabricante</CardTitle>
          <CardDescription>Progreso de PDFs por cada fabricante</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {porFabricante.map((fab) => {
              const fabConPdf = (fab.con_r2 || 0) + (fab.con_pdf_gridfs || 0);
              return (
                <div key={fab.fabricante_id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{fab.fabricante_nombre}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant={fab.porcentaje_completo === 100 ? "default" : "secondary"}>
                        {fab.porcentaje_completo}%
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {fabConPdf}/{fab.total}
                      </span>
                    </div>
                  </div>
                  <Progress value={fab.porcentaje_completo} className="h-2" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Documentos Incompletos */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="min-w-0">
              <CardTitle>📋 Documentos con Información Faltante</CardTitle>
              <CardDescription>
                {selectedTipoFaltante === 'todos' 
                  ? 'Documentos con cualquier dato incompleto'
                  : `Filtrando: ${TIPOS_FALTANTE[selectedTipoFaltante]?.label}`}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={selectedFabricante} onValueChange={(v) => {
                setSelectedFabricante(v);
                setPagination(p => ({...p, page: 1}));
              }}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Fabricante" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {porFabricante.map(f => (
                    <SelectItem key={f.fabricante_id} value={f.fabricante_id}>
                      {f.fabricante_nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedTipoFaltante} onValueChange={(v) => {
                setSelectedTipoFaltante(v);
                setPagination(p => ({...p, page: 1}));
              }}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPOS_FALTANTE).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" onClick={exportarLista}>
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {documentosIncompletos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>¡No hay documentos con este tipo de faltante!</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {documentosIncompletos.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => irADocumento(doc.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{doc.nombre || '(Sin nombre)'}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{doc.numero_referencia || `ID: ${doc.id?.slice(0, 8)}...`}</span>
                        <span>•</span>
                        <span>{doc.fabricante_nombre}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      {/* Mostrar badges de faltantes */}
                      {(doc.faltantes || []).map(faltante => {
                        const config = TIPOS_FALTANTE[faltante];
                        if (!config) return null;
                        const Icon = config.icon;
                        return (
                          <Badge 
                            key={faltante} 
                            variant="outline" 
                            className={`${config.bg} ${config.color} border-0 text-xs`}
                          >
                            <Icon className="h-3 w-3 mr-1" />
                            {config.label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Mostrando {documentosIncompletos.length} de {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination(p => ({...p, page: p.page - 1}))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Página {pagination.page} de {pagination.pages}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination(p => ({...p, page: p.page + 1}))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPDFDashboard;
